import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useToast } from '@chakra-ui/react';

import {
  fetchWorkers,
  fetchWorkerDetails,
  resetPausedWorker,
  fetchWorkerHealth,
  resumeBatchProcessing,
  resolveWorker,
  skipBatch,
  cancelWorker,
  Worker,
  WorkerStatus,
  WorkerType,
  ListWorkersParams,
} from 'api/workers';

import WorkersView from './Workers.view';

const WorkersContainer = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(1);
  const [limit] = useState(10);
  const [tabIndex, setTabIndex] = useState(0);
  const [statusFilter, setStatusFilter] = useState<WorkerStatus[] | undefined>(
    undefined,
  );
  const [typeFilter, setTypeFilter] = useState<WorkerType[] | undefined>(
    undefined,
  );
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Build query params based on tab
  const getQueryParams = (): ListWorkersParams => {
    const params: ListWorkersParams = {
      page,
      limit,
    };

    // Tab 1 = Paused Workers
    if (tabIndex === 1) {
      params.status = ['paused'];
    } else if (statusFilter && statusFilter.length > 0) {
      params.status = statusFilter;
    }

    if (typeFilter && typeFilter.length > 0) {
      params.type = typeFilter;
    }

    return params;
  };

  const queryParams = getQueryParams();

  // Fetch worker health
  const {
    data: healthData,
    isLoading: isLoadingHealth,
  } = useQuery('worker-health', fetchWorkerHealth, { refetchInterval: 30000 });

  // Resume batch processing
  const resumeMutation = useMutation(resumeBatchProcessing, {
    onSuccess: () => {
      toast({
        title: 'Resumed',
        description: 'Batch processing resumed successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      queryClient.invalidateQueries('worker-health');
    },
    onError: (error: any) => {
      toast({
        title: 'Resume Failed',
        description:
          error?.response?.data?.message || 'Failed to resume processing',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Fetch workers list
  const {
    data: workersData,
    isLoading: isLoadingWorkers,
    refetch: refetchWorkers,
  } = useQuery(['workers-list', queryParams], () => fetchWorkers(queryParams), {
    keepPreviousData: true,
  });

  // Fetch worker details
  const {
    data: workerDetails,
    isLoading: isLoadingDetails,
    refetch: refetchDetails,
  } = useQuery(
    ['worker-details', selectedWorker?._id],
    () => fetchWorkerDetails(selectedWorker!._id),
    {
      enabled: !!selectedWorker?._id && isDetailModalOpen,
    },
  );

  // Reset paused worker mutation
  const resetMutation = useMutation(resetPausedWorker, {
    onSuccess: (data) => {
      toast({
        title: 'Worker Reset',
        description: `Worker reset successfully. ${data.batchesReset} batches reset.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      queryClient.invalidateQueries('workers-list');
      refetchDetails();
    },
    onError: (error: any) => {
      toast({
        title: 'Reset Failed',
        description: error?.response?.data?.message || 'Failed to reset worker',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Resolve worker mutation
  const resolveMutation = useMutation(resolveWorker, {
    onSuccess: (data) => {
      toast({
        title: 'Worker Resolved',
        description: `${data.batchesSkipped} failed batches skipped.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      queryClient.invalidateQueries('workers-list');
      refetchDetails();
    },
    onError: (error: any) => {
      toast({
        title: 'Resolve Failed',
        description:
          error?.response?.data?.message || 'Failed to resolve worker',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Skip batch mutation
  const skipBatchMutation = useMutation(skipBatch, {
    onSuccess: () => {
      toast({
        title: 'Batch Skipped',
        description: 'Batch skipped, worker reset to continue.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      queryClient.invalidateQueries('workers-list');
      refetchDetails();
    },
    onError: (error: any) => {
      toast({
        title: 'Skip Failed',
        description: error?.response?.data?.message || 'Failed to skip batch',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Cancel worker mutation
  const cancelMutation = useMutation(cancelWorker, {
    onSuccess: () => {
      toast({
        title: 'Worker Cancelled',
        description: 'Worker cancelled and changes rolled back.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      queryClient.invalidateQueries('workers-list');
      setIsDetailModalOpen(false);
      setSelectedWorker(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Cancel Failed',
        description:
          error?.response?.data?.message || 'Failed to cancel worker',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleTabChange = (index: number) => {
    setTabIndex(index);
    setPage(1);
  };

  const handlePageChange: React.Dispatch<React.SetStateAction<number>> = (
    value,
  ) => {
    setPage(value);
  };

  const handleRowClick = (worker: Worker) => {
    setSelectedWorker(worker);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedWorker(null);
  };

  const handleRefresh = () => {
    refetchWorkers();
  };

  const handleResetWorker = (workerId: string) => {
    resetMutation.mutate(workerId);
  };

  const handleResolveWorker = (workerId: string) => {
    resolveMutation.mutate(workerId);
  };

  const handleSkipBatch = (workerId: string, batchId: string) => {
    skipBatchMutation.mutate({ workerId, batchId });
  };

  const handleCancelWorker = (workerId: string) => {
    cancelMutation.mutate(workerId);
  };

  const handleResume = () => {
    resumeMutation.mutate();
  };

  const handleStatusFilterChange = (statuses: WorkerStatus[] | undefined) => {
    setStatusFilter(statuses);
    setPage(1);
  };

  const handleTypeFilterChange = (types: WorkerType[] | undefined) => {
    setTypeFilter(types);
    setPage(1);
  };

  const viewProps = {
    workers: workersData?.workers || [],
    total: workersData?.total || 0,
    totalPages: workersData?.totalPages || 0,
    currentPage: page,
    isLoading: isLoadingWorkers,
    tabIndex,
    statusFilter,
    typeFilter,
    selectedWorker,
    workerDetails,
    isLoadingDetails,
    isDetailModalOpen,
    isResetting: resetMutation.isLoading,
    isResolving: resolveMutation.isLoading,
    isSkipping: skipBatchMutation.isLoading,
    isCancelling: cancelMutation.isLoading,
    health: healthData,
    isLoadingHealth,
    isResuming: resumeMutation.isLoading,
    onTabChange: handleTabChange,
    onPageChange: handlePageChange,
    onRowClick: handleRowClick,
    onCloseModal: handleCloseModal,
    onRefresh: handleRefresh,
    onResetWorker: handleResetWorker,
    onResolveWorker: handleResolveWorker,
    onSkipBatch: handleSkipBatch,
    onCancelWorker: handleCancelWorker,
    onResume: handleResume,
    onStatusFilterChange: handleStatusFilterChange,
    onTypeFilterChange: handleTypeFilterChange,
  };

  return <WorkersView {...viewProps} />;
};

export default WorkersContainer;
